import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';

import { FormElement, Label } from '@dashboard/components/Form';

import ConfirmationCheckoutMessageEditor from './ConfirmationCheckoutMessageEditor';
import { isEmpty } from 'lodash';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import FormError from '@dashboard/components/Formik/components/FormError';
import { usePreview } from '@dashboard/context/PreviewContext';
import { Checkbox, Radio, TextInput } from '@dashboard/components/Formik';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import TabButton from '@dashboard/components/TabButton';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Features from '@dashboard/enums/features';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import { productTypes } from '@dashboard/enums/product-types';
import usePage from '@/dashboard/hooks/usePage';

function getDefaultConfirmationTitle(checkout) {
  if (checkout.type === PAGE_TYPES.FORM) {
    return 'Submission confirmed!';
  } else if (checkout.type === PAGE_TYPES.EVENT) {
    return 'Booking confirmed!';
  } else if (checkout.type === PAGE_TYPES.CHECKOUT) {
    if (checkout.productType === productTypes.CHARGE) {
      return 'Payment confirmed!';
    } else if (checkout.productType === productTypes.SUBSCRIPTION) {
      return 'Subscription confirmed!';
    }
  }
}

const CheckoutConfirmationMessageForm = () => {
  const { page, updatePage } = usePage();

  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const checkoutValues = {
    showFilesList: page.showFilesList,
    showViewDownloadsButton: page.showViewDownloadsButton,
    afterPaymentAction: page.afterPaymentAction,
    confirmationCheckoutTitle: page.confirmationCheckoutTitle,
    confirmationCheckoutMessage: page.confirmationCheckoutMessage,
    customizeCheckoutConfirmation: page.customizeCheckoutConfirmation,
    showConfirmationEventName: page.showConfirmationEventName,
    showConfirmationEventDateTime: page.showConfirmationEventDateTime,
    showConfirmationEventLocation: page.showConfirmationEventLocation,
    showConfirmationEventTickets: page.showConfirmationEventTickets,
  };

  return (
    <Formik
      initialValues={checkoutValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          if (data.afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION) {
            setPreview({
              type: 'confirmationCheckoutMessage',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              showFilesList: data.showFilesList,
              showViewDownloadsButton: data.showViewDownloadsButton,
              afterPaymentAction: data.afterPaymentAction,
              confirmationCheckoutTitle: data.confirmationCheckoutTitle,
              confirmationCheckoutMessage: data.confirmationCheckoutMessage,
              customizeCheckoutConfirmation: data.customizeCheckoutConfirmation,
              showConfirmationEventName: data.showConfirmationEventName,
              showConfirmationEventDateTime: data.showConfirmationEventDateTime,
              showConfirmationEventLocation: data.showConfirmationEventLocation,
              showConfirmationEventTickets: data.showConfirmationEventTickets,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, setFieldValue, handleSubmit, setValues, isSubmitting }) => {
        useEffect(() => {
          setFieldValue('afterPaymentAction', AfterPaymentActionEnum.CONFIRMATION);
        }, []);

        return (
          <Form onSubmit={handleSubmit}>
            <TabGroup
              defaultIndex={values.customizeCheckoutConfirmation ? 1 : 0}
              onChange={index => {
                switch (index) {
                  case 0:
                    setValues({
                      ...checkoutValues,
                      afterPaymentAction: AfterPaymentActionEnum.CONFIRMATION,
                      customizeCheckoutConfirmation: false,
                    });
                    break;
                  case 1:
                    setValues({
                      ...checkoutValues,
                      afterPaymentAction: AfterPaymentActionEnum.CONFIRMATION,
                      ...(!values.confirmationCheckoutTitle && {
                        confirmationCheckoutTitle: getDefaultConfirmationTitle(page),
                      }),
                      customizeCheckoutConfirmation: true,
                    });
                    break;
                }
              }}
            >
              <TabList className="flex mt-1 gap-x-2">
                <Tab className="w-1/2 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Default message</TabButton>}
                </Tab>

                <Tab className="w-1/2 outline-none">
                  {({ selected }) => <TabButton selected={selected}>Custom message</TabButton>}
                </Tab>
              </TabList>

              <TabPanels>
                {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.FORM) && (
                  <TabPanel className="mt-4">
                    <FormElement>
                      <Label htmlFor="downloads">
                        File downloads{' '}
                        <InfoTooltip>
                          If you have files attached to this checkout, you can choose to display
                          them as a list or have a button that links to the files.
                        </InfoTooltip>
                      </Label>

                      <Radio.Group name="downloads" id="downloads">
                        <Radio.Wrapper>
                          <Radio.Input
                            value="showFilesList"
                            id="showFilesList"
                            name="downloads"
                            checked={values.showFilesList}
                            onChange={e => {
                              setFieldValue('showFilesList', e.target.checked);

                              setFieldValue('showViewDownloadsButton', !e.target.checked);
                            }}
                          />

                          <Radio.Label htmlFor="showFilesList">List of files</Radio.Label>
                        </Radio.Wrapper>

                        <Radio.Wrapper>
                          <Radio.Input
                            value="showViewDownloadsButton"
                            id="showViewDownloadsButton"
                            name="downloads"
                            checked={values.showViewDownloadsButton}
                            onChange={e => {
                              setFieldValue('showViewDownloadsButton', e.target.checked);

                              setFieldValue('showFilesList', !e.target.checked);
                            }}
                          />

                          <Radio.Label htmlFor="showViewDownloadsButton">
                            View downloads button
                          </Radio.Label>
                        </Radio.Wrapper>
                      </Radio.Group>
                    </FormElement>

                    <FormSubmit>
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </FormSubmit>
                  </TabPanel>
                )}

                {page.type === PAGE_TYPES.EVENT && (
                  <TabPanel className="mt-4">
                    <Label>Show/hide</Label>
                    <Checkbox.Wrapper>
                      <Checkbox id="showConfirmationEventName" name="showConfirmationEventName" />
                      <Checkbox.Label htmlFor="showConfirmationEventName">
                        Event name
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventDateTime"
                        name="showConfirmationEventDateTime"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventDateTime">
                        Date and time
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventLocation"
                        name="showConfirmationEventLocation"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventLocation">
                        Location
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <Checkbox.Wrapper>
                      <Checkbox
                        id="showConfirmationEventTickets"
                        name="showConfirmationEventTickets"
                      />
                      <Checkbox.Label htmlFor="showConfirmationEventTickets">
                        Tickets purchased
                      </Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormSubmit>
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </FormSubmit>
                  </TabPanel>
                )}

                <TabPanel className="mt-4">
                  <CanUseFeature feature={Features.CUSTOMIZE_CONFIRMATION}>
                    <FormElement>
                      <Label htmlFor="confirmationCheckoutTitle">Confirmation heading</Label>
                      <TextInput
                        name="confirmationCheckoutTitle"
                        id="confirmationCheckoutTitle"
                        type="text"
                        required
                        border
                      />
                      <FormError name="confirmationCheckoutTitle" />
                    </FormElement>

                    <FormElement>
                      <div className="flex justify-between items-baseline">
                        <Label>Confirmation message</Label>
                        <span className="text-xs text-gray-500 font-normal text-right ml-auto">
                          Insert variables by typing{' '}
                          <code className="font-mono bg-gray-100 px-1 py-0.5 text-chocolate-1000">
                            {'{'}
                          </code>
                        </span>
                      </div>
                      <ConfirmationCheckoutMessageEditor
                        id="confirmationCheckoutMessage"
                        name="confirmationCheckoutMessage"
                        initialValue="Thank you for your payment"
                        page={page}
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                      <FormError name="confirmationCheckoutMessage" />
                    </FormElement>

                    {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.FORM) && (
                      <FormElement>
                        <Label htmlFor="downloads">
                          File downloads{' '}
                          <InfoTooltip>
                            If you have files attached to this checkout, you can choose to display
                            them as a list or have a button that links to the files.
                          </InfoTooltip>
                        </Label>

                        <Radio.Group name="downloads" id="downloads">
                          <Radio.Wrapper>
                            <Radio.Input
                              value="showFilesList"
                              id="showFilesList"
                              name="downloads"
                              checked={values.showFilesList}
                              onChange={e => {
                                setFieldValue('showFilesList', e.target.checked);

                                setFieldValue('showViewDownloadsButton', !e.target.checked);
                              }}
                            />

                            <Radio.Label htmlFor="showFilesList">List of files</Radio.Label>
                          </Radio.Wrapper>

                          <Radio.Wrapper>
                            <Radio.Input
                              value="showViewDownloadsButton"
                              id="showViewDownloadsButton"
                              name="downloads"
                              checked={values.showViewDownloadsButton}
                              onChange={e => {
                                setFieldValue('showViewDownloadsButton', e.target.checked);

                                setFieldValue('showFilesList', !e.target.checked);
                              }}
                            />

                            <Radio.Label htmlFor="showViewDownloadsButton">
                              View downloads button
                            </Radio.Label>
                          </Radio.Wrapper>
                        </Radio.Group>
                      </FormElement>
                    )}

                    {page.type === PAGE_TYPES.EVENT && (
                      <>
                        <Label>Show/hide</Label>
                        <Checkbox.Wrapper>
                          <Checkbox
                            id="showConfirmationEventName"
                            name="showConfirmationEventName"
                          />
                          <Checkbox.Label htmlFor="showConfirmationEventName">
                            Event name
                          </Checkbox.Label>
                        </Checkbox.Wrapper>

                        <Checkbox.Wrapper>
                          <Checkbox
                            id="showConfirmationEventDateTime"
                            name="showConfirmationEventDateTime"
                          />
                          <Checkbox.Label htmlFor="showConfirmationEventDateTime">
                            Date and time
                          </Checkbox.Label>
                        </Checkbox.Wrapper>

                        <Checkbox.Wrapper>
                          <Checkbox
                            id="showConfirmationEventLocation"
                            name="showConfirmationEventLocation"
                          />
                          <Checkbox.Label htmlFor="showConfirmationEventLocation">
                            Location
                          </Checkbox.Label>
                        </Checkbox.Wrapper>

                        <Checkbox.Wrapper>
                          <Checkbox
                            id="showConfirmationEventTickets"
                            name="showConfirmationEventTickets"
                          />
                          <Checkbox.Label htmlFor="showConfirmationEventTickets">
                            Tickets purchased
                          </Checkbox.Label>
                        </Checkbox.Wrapper>
                      </>
                    )}

                    <FormElement>{status && status}</FormElement>

                    <FormSubmit>
                      <FormSubmit.Button>Save</FormSubmit.Button>
                      <FormSubmit.State visible={!!dirty && !isSubmitting} />
                    </FormSubmit>
                  </CanUseFeature>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CheckoutConfirmationMessageForm;
